import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Check, ArrowLeft, Sparkles, Zap, Rocket, Star, Building2, Loader2, ArrowRight,
} from "lucide-react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { Helmet } from "react-helmet-async";
import Seo from "../components/Seo";
import { formatInr } from "../lib/usePricingCatalog";
import { FreeTrialText, ReferralRewardText } from "../components/PricingCopy";

const ICONS = {
  free: Sparkles,
  starter: Zap,
  creator: Rocket,
  pro: Star,
  studio: Building2,
};

const fmtInr = formatInr;

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [pl, us] = await Promise.all([
          api.get("/billing/plans"),
          user ? api.get("/billing/usage").catch(() => ({ data: null })) : Promise.resolve({ data: null }),
        ]);
        setPlans(pl.data.plans);
        if (us.data) setCurrent(us.data.plan.key);
      } catch (err) {
        if (process.env.NODE_ENV !== "production") console.warn("Load plans failed:", err?.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  const buy = async (planKey) => {
    if (!user) { navigate("/"); return; }
    setBuying(planKey);
    try {
      // Keep the client intent across refreshes/tabs; the server owns the quote.
      const storageKey = `linguasub-checkout-${user.user_id}`;
      let intent;
      try { intent = JSON.parse(localStorage.getItem(storageKey)); } catch { intent = null; }
      if (!intent || intent.planKey !== planKey) {
        intent = { planKey, requestId: crypto.randomUUID(), acknowledgedOrderId: localStorage.getItem(`linguasub-confirmed-order-${user.user_id}`) || undefined };
        localStorage.setItem(storageKey, JSON.stringify(intent));
      }
      const { data } = await api.post("/billing/checkout", {
        plan_key: planKey, origin_url: window.location.origin, request_id: intent.requestId,
        acknowledged_order_id: intent.acknowledgedOrderId,
      });
      localStorage.setItem(storageKey, JSON.stringify({ ...intent, orderId: data.session_id }));
      if (data.completed) {
        navigate(`/payment/success?session_id=${encodeURIComponent(data.session_id)}`);
        return;
      }
      if (data.order_state && data.order_state !== "pending") {
        localStorage.removeItem(storageKey);
        throw new Error("That checkout is closed. Choose your plan again to begin a new purchase.");
      }
      // 2. Boot the drop-in SDK (loaded in index.html) in the same mode
      // the backend used to create the order. Sandbox in dev, production
      // once we go live — the mismatch is the #1 debug pain per Cashfree
      // docs.
      const CashfreeCtor = window.Cashfree;
      if (typeof CashfreeCtor !== "function") {
        throw new Error("Payments SDK didn't load — check your ad blocker");
      }
      if (!data.payment_session_id || !["sandbox", "production"].includes(data.mode)) {
        throw new Error("The checkout session is incomplete. Please try again.");
      }
      if (data.referral_discount_applied) {
        toast.success(`Referral reward applied: ${data.referral_discount_percent}% off. Total ₹${fmtInr(data.amount_inr)}.`);
      }
      const cashfree = CashfreeCtor({ mode: data.mode });
      // 3. Redirect the tab into Cashfree's hosted checkout. Once the
      // customer finishes (success/failure/dropped), Cashfree bounces
      // them to our /payment/success?session_id=... page which polls
      // the backend for the authoritative status.
      const result = await cashfree.checkout({
        paymentSessionId: data.payment_session_id,
        redirectTarget: "_self",
      });
      if (result?.error) {
        throw new Error(result.error.message || "Checkout failed. Please try again.");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error((typeof detail === "string" ? detail : detail?.message) || e?.message || "Could not start checkout");
      setBuying(null);
    }
  };

  return (
    <div className="grain-bg min-h-screen bg-zinc-950 text-white p-6 md:p-12" data-testid="pricing-page">
      <Seo
        path="/pricing"
        title="Pricing — Free, Starter, Creator, Pro, Studio plans"
        description={plans.length ? plans.map((p) => `${p.name}: ₹${fmtInr(p.amount_inr)} for ${p.minutes} minutes${p.allowance_type === "one_time" ? " once for new users" : `, valid for ${p.duration_days} days`}`).join(". ") : "Current INR plans and a one-time free trial. No automatic renewal."}
      />
      {plans.length > 0 && <Helmet><script type="application/ld+json">{JSON.stringify({
        "@context": "https://schema.org", "@type": "SoftwareApplication",
        name: "LinguaSub", applicationCategory: "MultimediaApplication", operatingSystem: "Web",
        offers: plans.map((p) => ({ "@type": "Offer", name: p.name, price: String(p.amount_inr),
          priceCurrency: "INR", description: `${p.minutes} minutes — ${p.allowance_type === "one_time" ? "one time for new users" : `valid for ${p.duration_days} days`}` })),
      })}</script></Helmet>}
      <div className="max-w-7xl mx-auto pt-4">
        <Link
          to={user ? "/dashboard" : "/"}
          className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-zinc-500 hover:text-white mb-10 transition-colors"
          data-testid="pricing-back"
        >
          <ArrowLeft className="w-3 h-3" /> Back
        </Link>

        <div className="mb-14">
          <div className="text-[10px] font-mono uppercase tracking-widest text-blue-400 mb-3">
            Pricing · prepaid minute packs
          </div>
          <h1 className="font-heading text-5xl md:text-6xl font-black tracking-tighter mb-4">
            Pay per minute you actually use.
          </h1>
          <p className="text-zinc-400 max-w-2xl text-sm leading-relaxed">
            <FreeTrialText />. Paid packs use the validity shown below, with no automatic renewal.
            Existing remaining balances are preserved; each paid purchase keeps its own expiry.
          </p>
        </div>

        {loading ? (
          <div className="flex items-center gap-3 text-zinc-500 text-sm" data-testid="pricing-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading plans...
          </div>
        ) : (
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 items-stretch"
            data-testid="plan-grid"
          >
            {plans.map((p) => {
              const Icon = ICONS[p.key] || Sparkles;
              const isCurrent = current === p.key;
              const isFeatured = Boolean(p.featured);
              return (
                <div
                  key={p.key}
                  data-testid={`plan-card-${p.key}`}
                  className={
                    "relative flex flex-col p-6 md:p-7 transition-all duration-200 " +
                    (isFeatured
                      // Featured card: gradient hairline, blue glow, lifted.
                      ? "bg-gradient-to-b from-blue-500/10 via-zinc-900/60 to-zinc-900/40 border-2 border-blue-500/60 shadow-[0_0_60px_-15px_rgba(59,130,246,0.5)] lg:scale-[1.03] lg:z-10"
                      : "bg-zinc-900/40 border border-zinc-800 hover:border-zinc-700")
                  }
                >
                  {isFeatured && (
                    <div
                      className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-500 text-white text-[10px] font-mono uppercase tracking-widest px-3 py-1 rounded-sm shadow-lg"
                      data-testid={`featured-badge-${p.key}`}
                    >
                      Most popular
                    </div>
                  )}
                  {isCurrent && (
                    <div
                      className="absolute -top-3 right-4 bg-emerald-500 text-zinc-950 text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-sm"
                      data-testid={`current-badge-${p.key}`}
                    >
                      Current
                    </div>
                  )}

                  <div className="mb-5">
                    <Icon
                      className={`w-6 h-6 mb-3 ${isFeatured ? "text-blue-400" : "text-zinc-500"}`}
                    />
                    <div className="font-heading text-2xl font-bold tracking-tight text-white">
                      {p.name}
                    </div>
                    <div className="text-[11px] font-mono text-zinc-500 mt-1">
                      {p.minutes} minutes included
                    </div>
                  </div>

                  <div className="mb-4">
                    <div className="flex items-baseline gap-1.5">
                      <span className="font-heading text-2xl font-bold text-zinc-500">
                        {"\u20B9"}
                      </span>
                      <span className="font-heading text-5xl font-black tracking-tighter">
                        {fmtInr(p.amount_inr)}
                      </span>
                      {p.amount_inr > 0 && (
                        <span className="text-xs font-mono text-zinc-500">/ pack</span>
                      )}
                    </div>
                    {p.per_minute_inr > 0 && (
                      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-2">
                        {"\u20B9"}{p.per_minute_inr.toFixed(2)} / minute
                      </div>
                    )}
                  </div>

                  {p.tagline && (
                    <p className="text-[13px] text-zinc-400 leading-relaxed mb-5 min-h-[3.5rem]">
                      {p.tagline}
                    </p>
                  )}

                  <ul className="space-y-2 mb-6 flex-1">
                    {p.features.map((f) => (
                      <li
                        key={f}
                        className="flex items-start gap-2 text-xs text-zinc-300 leading-relaxed"
                      >
                        <Check
                          className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${
                            isFeatured ? "text-blue-400" : "text-emerald-400"
                          }`}
                        />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>

                  {p.key === "free" ? (
                    <Button
                      onClick={() => navigate(user ? "/dashboard" : "/")}
                      data-testid={`plan-cta-${p.key}`}
                      className="w-full h-11 border border-zinc-800 bg-transparent text-white hover:bg-zinc-800 hover:text-white rounded-none transition-colors duration-200 group inline-flex items-center justify-center gap-2"
                    >
                      {user ? "Go to dashboard" : "Start free"}
                      <ArrowRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </Button>
                  ) : (
                    <Button
                      onClick={() => buy(p.key)}
                      disabled={buying !== null}
                      data-testid={`plan-cta-${p.key}`}
                      className={
                        "w-full h-11 rounded-none font-semibold text-sm transition-all duration-200 inline-flex items-center justify-center gap-2 group " +
                        (isFeatured
                          ? "bg-blue-500 text-white hover:bg-blue-400 shadow-[0_10px_30px_-10px_rgba(59,130,246,0.7)]"
                          : "bg-transparent border border-zinc-800 text-white hover:bg-zinc-800")
                      }
                    >
                      {buying === p.key
                        ? "Redirecting..."
                        : isCurrent
                          ? "Add more minutes"
                          : (
                            <>
                              Get {p.name}
                              <ArrowRight className="w-3.5 h-3.5 opacity-70 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
                            </>
                          )}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-14 space-y-3">
          {/* Referral program teaser — sits directly under the pricing grid
              where value-conscious shoppers will notice it as they're
              already thinking about cost. Both text and CTA are subtle so
              they don't compete with the plan cards for attention. */}
          <div
            data-testid="pricing-referral-line"
            className="border border-blue-500/30 bg-blue-500/5 p-4 md:p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-center sm:text-left"
          >
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 shrink-0 grid place-items-center rounded-full bg-blue-500/15 text-blue-300">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7"/></svg>
              </div>
              <div className="text-sm text-white">
                <ReferralRewardText />
                <div className="text-[11px] text-zinc-500 font-mono mt-0.5">
                  One reward per eligible purchase, applied automatically. No stacking. Referred buyers pay the advertised price.
                </div>
              </div>
            </div>
            <Link
              to={user ? "/dashboard" : "/"}
              data-testid="pricing-referral-cta"
              className="shrink-0 inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest px-3 py-2 border border-blue-500/40 text-blue-200 hover:bg-blue-500/10 transition-colors duration-150"
            >
              Get your link
              <ArrowRight className="w-3 h-3" />
            </Link>
          </div>

          <div className="text-center space-y-2">
            <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500">
              All plans · 21 languages · SRT + VTT export · Subtitled MP4
            </div>
            <div className="text-[11px] font-mono text-zinc-600">
              Prices in INR ({"\u20B9"}). Secure checkout with Cashfree. No automatic renewal.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
